import { useFlags } from 'gatsby-plugin-launchdarkly';
import { useEffect, useState } from 'react';

import { useIsFeaturedEnabledByVWO } from './hooks/useVWO';

export const FLAG_NAME = 'cartRedesign';

enum FeatureFlagStatus {
  ON = 'on',
  OFF = 'off',
}

/**
 * Custom hook to check if the cart redesign feature flag is enabled
 */
const useIsEnabledByQueryParam = (featureID: string) => {
  const [isEnabled, setIsEnabled] = useState(false);

  useEffect(() => {
    // Read parameters from the URL
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.has(featureID)) {
      // Persist parameters in session storage
      const value = urlParams.get(featureID);

      // Only accept 'on' or 'off' values to turn the flag on or off
      if (value === FeatureFlagStatus.ON || value === FeatureFlagStatus.OFF) {
        sessionStorage.setItem(featureID, value);
      }
    }

    setIsEnabled(readFromStorage(featureID));
  }, [featureID]);

  useEffect(() => {
    const handler = (event: StorageEvent) => {
      if (event.storageArea === sessionStorage) {
        setIsEnabled(readFromStorage(featureID));
      }
    };

    window.addEventListener('storage', handler);

    return () => {
      window.removeEventListener('storage', handler);
    };
  }, [featureID]);

  return isEnabled;
};

const readFromStorage = (featureID: string): boolean => {
  const flag = sessionStorage.getItem(featureID);
  return flag === FeatureFlagStatus.ON;
};

export type Features =
  // enables the new cart experience for all users
  | 'cartRedesign'
  // Enables the Purchase options drawer in mobile
  | 'preCartExperience'
  // Enables the new checkout experience
  | 'checkoutRedesign'
  // Enables new payment intent methods
  | 'enableStripeUpgrade'
  // Any other key
  | (string & {});

export type UseIsFeatureEnabledResult = {
  isLoading: boolean;
  isEnabled: boolean;
};

const useIsFeatureEnabled = (
  featureID: Features,
): UseIsFeatureEnabledResult => {
  // only boolean flags are supported
  const flags = useFlags();
  const [isMounted, setIsMounted] = useState(false);

  const isEnabledByLaunchDarkly = flags[featureID as keyof typeof flags];
  const isEnabledByParam = useIsEnabledByQueryParam(featureID);
  const isEnabledByVWO = useIsFeaturedEnabledByVWO(featureID);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // If the feature flag is enabled by the URL parameter, make sure to enable the new experience
  // this is useful for testing purposes in production where we can enable the new experience for a specific user
  if (isEnabledByParam) {
    return {
      isLoading: false,
      isEnabled: true,
    };
  }

  const isLoading = !isMounted || isEnabledByVWO.isLoading;

  // On the other hand if the feature flag is enabled by LaunchDarkly and the VWO experiment, then we should enable the new experience for all users.
  // this is useful for A/B testing purposes where we want to compare the new experience with the old one
  return isEnabledByLaunchDarkly === true
    ? {
        isLoading,
        isEnabled: Boolean(isEnabledByVWO.result),
      }
    : { isLoading, isEnabled: false };
};

export default useIsFeatureEnabled;

declare global {
  interface Window {
    EXPERIMENTS: Record<string, boolean>;
  }
}
